import React, { createContext, useState, useEffect, useContext } from 'react';

const AuthContext = createContext();

// Определяем, запущено ли локально или на production
// В production на Vercel всегда используем реальный API
const getIsLocal = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.');
};

const IS_LOCAL = getIsLocal();

// Mock данные для локальной разработки - СОХРАНЯЕМ В LOCALSTORAGE
const getMockUsers = () => {
  try {
    const stored = localStorage.getItem('mockUsers');
    console.log('📦 Загружаем пользователей из localStorage:', stored);
    const parsed = stored ? JSON.parse(stored) : [];
    return parsed.map(user => ({
      ...user,
      waitingList: Array.isArray(user.waitingList) ? user.waitingList : [],
      favorites: Array.isArray(user.favorites) ? user.favorites : [],
      collections: Array.isArray(user.collections) ? user.collections : [],
    }));
  } catch (error) {
    console.error('❌ Ошибка загрузки mockUsers:', error);
    return [];
  }
};

const saveMockUsers = (users) => {
  try {
    console.log('💾 Сохраняем пользователей в localStorage:', users);
    localStorage.setItem('mockUsers', JSON.stringify(users));
  } catch (error) {
    console.error('❌ Ошибка сохранения mockUsers:', error);
  }
};

const getNextUserId = () => {
  const users = getMockUsers();
  if (users.length === 0) return 1;
  return Math.max(...users.map(u => u.id)) + 1;
};

// Mock API функции
const createMockResponse = (data, options = {}) => {
  const { ok = true, status = ok ? 200 : 400, statusText = ok ? 'OK' : 'Bad Request' } = options;
  return {
    ok,
    status,
    statusText,
    json: async () => data,
    text: async () => JSON.stringify(data)
  };
};

const ensureWaitingList = (user) => {
  if (!user) return null;
  return {
    ...user,
    waitingList: Array.isArray(user.waitingList) ? user.waitingList : [],
    favorites: Array.isArray(user.favorites) ? user.favorites : [],
    collections: Array.isArray(user.collections) ? user.collections : [],
  };
};

const mockApi = {
  register: async (email, password) => {
    console.log('🔧 Mock API: register вызван для', email);
    await new Promise(resolve => setTimeout(resolve, 500));
    const mockUsers = getMockUsers();
    const existingUser = mockUsers.find(u => u.email === email);
    if (existingUser) {
      console.log('⚠️ Пользователь уже существует:', email);
      return createMockResponse(
        { message: 'Пользователь уже существует' },
        { ok: false, status: 409, statusText: 'Conflict' }
      );
    }
    const newUser = { 
      id: getNextUserId(), 
      email, 
      password, 
      nickname: null, 
      avatar: null,
      instagram: null,
      twitter: null,
      discord: null,
      bio: null,
      cryptoWallet: null,
      backgroundPhoto: null,
      waitingList: [],
      favorites: [],
      collections: [],
    };
    mockUsers.push(newUser);
    saveMockUsers(mockUsers);
    const token = `mock_token_${newUser.id}`;
    console.log('✅ Пользователь зарегистрирован:', newUser);
    return createMockResponse({
      token,
      user: ensureWaitingList({ 
        id: newUser.id, 
        email: newUser.email, 
        nickname: newUser.nickname, 
        avatar: newUser.avatar,
        instagram: newUser.instagram,
        twitter: newUser.twitter,
        discord: newUser.discord,
        bio: newUser.bio,
        cryptoWallet: newUser.cryptoWallet,
        backgroundPhoto: newUser.backgroundPhoto,
        waitingList: newUser.waitingList,
        favorites: newUser.favorites,
        collections: newUser.collections,
      })
    });
  },
  
  login: async (email, password) => {
    console.log('🔧 Mock API: login вызван для', email);
    await new Promise(resolve => setTimeout(resolve, 500));
    const mockUsers = getMockUsers();
    console.log('📋 Все пользователи в системе:', mockUsers);
    const user = mockUsers.find(u => u.email === email && u.password === password);
    if (!user) {
      console.log('⚠️ Неверный email или пароль');
      return createMockResponse(
        { message: 'Неверный email или пароль' },
        { ok: false, status: 401, statusText: 'Unauthorized' }
      );
    }
    const token = `mock_token_${user.id}`;
    console.log('✅ Пользователь вошел:', user);
    return createMockResponse({
      token,
      user: ensureWaitingList({ 
        id: user.id, 
        email: user.email, 
        nickname: user.nickname, 
        avatar: user.avatar,
        instagram: user.instagram,
        twitter: user.twitter,
        discord: user.discord,
        bio: user.bio,
        cryptoWallet: user.cryptoWallet,
        backgroundPhoto: user.backgroundPhoto,
        waitingList: user.waitingList,
        favorites: user.favorites,
        collections: user.collections,
      })
    });
  },
  
  getMe: async (token) => {
    console.log('🔧 Mock API: getMe вызван с токеном', token);
    await new Promise(resolve => setTimeout(resolve, 300));
    if (!token || !token.startsWith('mock_token_')) {
      console.log('⚠️ Недействительный токен');
      return createMockResponse(
        { message: 'Недействительный токен' },
        { ok: false, status: 401, statusText: 'Unauthorized' }
      );
    }
    const userId = parseInt(token.replace('mock_token_', ''));
    console.log('🔍 Ищем пользователя с ID:', userId);
    const mockUsers = getMockUsers();
    console.log('📋 Все пользователи при getMe:', mockUsers);
    const user = mockUsers.find(u => u.id === userId);
    if (!user) {
      console.log('❌ Пользователь не найден с ID:', userId);
      return createMockResponse(
        { message: 'Пользователь не найден' },
        { ok: false, status: 404, statusText: 'Not Found' }
      );
    }
    console.log('✅ Пользователь найден:', user);
    return createMockResponse({
      user: ensureWaitingList({ 
        id: user.id, 
        email: user.email, 
        nickname: user.nickname, 
        avatar: user.avatar,
        instagram: user.instagram,
        twitter: user.twitter,
        discord: user.discord,
        bio: user.bio,
        cryptoWallet: user.cryptoWallet,
        backgroundPhoto: user.backgroundPhoto,
        waitingList: user.waitingList,
        favorites: user.favorites,
        collections: user.collections,
      })
    });
  },

  // Функция для обновления профиля
  updateProfile: async (token, updateData) => {
    console.log('🔧 Mock API: updateProfile вызван');
    console.log('   Токен:', token);
    console.log('   Update data:', updateData);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (!token || !token.startsWith('mock_token_')) {
      console.log('⚠️ Недействительный токен при обновлении');
      return createMockResponse(
        { message: 'Недействительный токен' },
        { ok: false, status: 401, statusText: 'Unauthorized' }
      );
    }
    
    const userId = parseInt(token.replace('mock_token_', ''));
    console.log('🔍 Обновляем профиль пользователя с ID:', userId);
    
    const mockUsers = getMockUsers();
    console.log('📋 Все пользователи перед обновлением:', mockUsers);
    
    const userIndex = mockUsers.findIndex(u => u.id === userId);
    
    if (userIndex === -1) {
      console.log('❌ Пользователь не найден при обновлении, ID:', userId);
      console.log('   Доступные ID:', mockUsers.map(u => u.id));
      return createMockResponse(
        { message: 'Пользователь не найден' },
        { ok: false, status: 404, statusText: 'Not Found' }
      );
    }
    
    // Обновляем данные пользователя
    Object.keys(updateData).forEach(key => {
      mockUsers[userIndex][key] = updateData[key];
    });
    
    console.log('✏️ Обновленный пользователь:', mockUsers[userIndex]);
    
    // ВАЖНО: Сохраняем изменения в localStorage
    saveMockUsers(mockUsers);
    
    // Проверяем, что данные сохранились
    const savedUsers = getMockUsers();
    const savedUser = savedUsers.find(u => u.id === userId);
    console.log('✅ Пользователь после сохранения:', savedUser);
    
    return createMockResponse({
      user: ensureWaitingList({ 
        id: mockUsers[userIndex].id, 
        email: mockUsers[userIndex].email, 
        nickname: mockUsers[userIndex].nickname, 
        avatar: mockUsers[userIndex].avatar,
        instagram: mockUsers[userIndex].instagram,
        twitter: mockUsers[userIndex].twitter,
        discord: mockUsers[userIndex].discord,
        bio: mockUsers[userIndex].bio,
        cryptoWallet: mockUsers[userIndex].cryptoWallet,
        backgroundPhoto: mockUsers[userIndex].backgroundPhoto,
        waitingList: mockUsers[userIndex].waitingList,
        favorites: mockUsers[userIndex].favorites,
        collections: mockUsers[userIndex].collections,
      })
    });
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    console.log('🔐 Проверка авторизации, токен:', token);
    console.log('🌐 IS_LOCAL:', IS_LOCAL);
    console.log('🌐 Hostname:', typeof window !== 'undefined' ? window.location.hostname : 'SSR');
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      let response;
      
      if (IS_LOCAL) {
        console.log('🔧 LOCAL MODE: Используем mock API');
        response = await mockApi.getMe(token);
      } else {
        console.log('🌐 PRODUCTION MODE: Вызываем /api/auth/me');
        const apiUrl = '/api/auth/me';
        console.log('📡 Запрос к:', apiUrl);
        console.log('📡 Полный URL:', window.location.origin + apiUrl);
        
        try {
          response = await fetch(apiUrl, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log('📥 Ответ статус:', response.status, response.statusText);
          console.log('📥 Ответ headers:', Object.fromEntries(response.headers.entries()));
        } catch (fetchError) {
          console.error('❌ Fetch error:', fetchError);
          console.error('   Error type:', fetchError.constructor.name);
          console.error('   Error message:', fetchError.message);
          throw fetchError;
        }
      }

      if (!response) {
        throw new Error('No response received');
      }

      let data;
      try {
        const text = await response.text();
        console.log('📥 Ответ текст (raw):', text);
        if (text) {
          data = JSON.parse(text);
          console.log('📥 Ответ данные (parsed):', data);
        } else {
          data = { message: 'Empty response' };
        }
      } catch (parseError) {
        console.error('❌ Parse error:', parseError);
        console.error('   Response status:', response.status);
        console.error('   Response statusText:', response.statusText);
        data = { message: `Failed to parse response: ${parseError.message}` };
      }

      if (response.ok) {
        console.log('✅ Авторизация успешна, пользователь:', data.user);
        // Убеждаемся, что collections загружены
        const userWithCollections = ensureWaitingList({
          ...data.user,
          collections: Array.isArray(data.user.collections) ? data.user.collections : [],
          favorites: Array.isArray(data.user.favorites) ? data.user.favorites : [],
        });
        console.log('📦 Пользователь с collections:', userWithCollections.collections?.length || 0, 'коллекций');
        setUser(userWithCollections);
      } else {
        console.log('❌ Авторизация не удалась:', response.status, data);
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('❌ Auth check error:', error);
      console.error('Error details:', error.message, error.stack);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      let response;
      
      if (IS_LOCAL) {
        console.log('🔧 LOCAL MODE: Используем mock API для логина');
        response = await mockApi.login(email, password);
      } else {
        console.log('🌐 PRODUCTION MODE: Вызываем /api/auth/login');
        const apiUrl = '/api/auth/login';
        console.log('📡 Запрос к:', apiUrl);
        console.log('📡 Полный URL:', window.location.origin + apiUrl);
        
        try {
          response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          console.log('📥 Ответ статус:', response.status, response.statusText);
          console.log('📥 Ответ headers:', Object.fromEntries(response.headers.entries()));
        } catch (fetchError) {
          console.error('❌ Fetch error:', fetchError);
          console.error('   Error type:', fetchError.constructor.name);
          console.error('   Error message:', fetchError.message);
          throw fetchError;
        }
      }

      if (!response) {
        throw new Error('No response received');
      }

      let data;
      try {
        const text = await response.text();
        console.log('📥 Ответ текст (raw):', text);
        data = JSON.parse(text);
        console.log('📥 Ответ данные (parsed):', data);
      } catch (parseError) {
        console.error('❌ Parse error:', parseError);
        console.error('   Response status:', response.status);
        console.error('   Response statusText:', response.statusText);
        throw new Error(`Failed to parse response: ${parseError.message}`);
      }

      if (response.ok) {
        localStorage.setItem('token', data.token);
        const userWithCollections = ensureWaitingList({
          ...data.user,
          collections: Array.isArray(data.user.collections) ? data.user.collections : [],
          favorites: Array.isArray(data.user.favorites) ? data.user.favorites : [],
        });
        setUser(userWithCollections);
        console.log('✅ Вход выполнен успешно');
        return { success: true };
      } else {
        console.error('❌ Ошибка входа:', data.message);
        return { success: false, message: data.message || 'Ошибка входа' };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('Error details:', error.message, error.stack);
      return { success: false, message: 'Ошибка подключения к серверу: ' + error.message };
    }
  };

  const register = async (email, password) => {
    try {
      let response;
      
      if (IS_LOCAL) {
        console.log('🔧 LOCAL MODE: Используем mock API для регистрации');
        response = await mockApi.register(email, password);
      } else {
        console.log('🌐 PRODUCTION MODE: Вызываем /api/auth/register');
        const apiUrl = '/api/auth/register';
        console.log('📡 Запрос к:', apiUrl);
        console.log('📡 Полный URL:', window.location.origin + apiUrl);
        
        try {
          response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          console.log('📥 Ответ статус:', response.status, response.statusText);
          console.log('📥 Ответ headers:', Object.fromEntries(response.headers.entries()));
        } catch (fetchError) {
          console.error('❌ Fetch error:', fetchError);
          console.error('   Error type:', fetchError.constructor.name);
          console.error('   Error message:', fetchError.message);
          throw fetchError;
        }
      }

      if (!response) {
        throw new Error('No response received');
      }

      let data;
      try {
        const text = await response.text();
        console.log('📥 Ответ текст (raw):', text);
        data = JSON.parse(text);
        console.log('📥 Ответ данные (parsed):', data);
      } catch (parseError) {
        console.error('❌ Parse error:', parseError);
        console.error('   Response status:', response.status);
        console.error('   Response statusText:', response.statusText);
        throw new Error(`Failed to parse response: ${parseError.message}`);
      }

      if (response.ok) {
        localStorage.setItem('token', data.token);
        const userWithCollections = ensureWaitingList({
          ...data.user,
          collections: Array.isArray(data.user.collections) ? data.user.collections : [],
          favorites: Array.isArray(data.user.favorites) ? data.user.favorites : [],
        });
        setUser(userWithCollections);
        console.log('✅ Регистрация выполнена успешно');
        return { success: true };
      } else {
        console.error('❌ Ошибка регистрации:', data.message);
        return { success: false, message: data.message || 'Ошибка регистрации' };
      }
    } catch (error) {
      console.error('❌ Register error:', error);
      console.error('Error details:', error.message, error.stack);
      return { success: false, message: 'Ошибка подключения к серверу: ' + error.message };
    }
  };

  // Функция updateProfile - принимает объект с любыми полями
  const updateProfile = async (updateData) => {
    console.log('🔄 updateProfile вызван');
    console.log('   Update data:', updateData);
    console.log('   Текущий пользователь:', user);
    
    try {
      const token = localStorage.getItem('token');
      console.log('   Токен из localStorage:', token);
      
      if (!token) {
        console.log('❌ Токен не найден');
        return { success: false, message: 'Не авторизован' };
      }

      let response;
      
      if (IS_LOCAL) {
        console.log('🔧 LOCAL MODE: Используем mock API для обновления профиля');
        response = await mockApi.updateProfile(token, updateData);
      } else {
        response = await fetch('/api/auth/update-profile', {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(updateData),
        });
      }

      const data = await response.json();
      console.log('📥 Ответ от API:', data);

      if (response.ok) {
        // ВАЖНО: обновляем состояние пользователя
        console.log('✅ Обновляем состояние пользователя на:', data.user);
        setUser(ensureWaitingList(data.user));
        return { success: true };
      } else {
        console.error('❌ Ошибка обновления:', data.message);
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, message: 'Ошибка обновления профиля' };
    }
  };

  const addToWaitingList = async (slug) => {
    console.log('⭐ addToWaitingList вызван для slug:', slug);
    if (!slug) {
      return { success: false, message: 'Slug обязателен' };
    }

    if (!user) {
      console.warn('⚠️ Пользователь не авторизован — waiting list недоступен');
      return { success: false, message: 'Необходимо войти в аккаунт', requireAuth: true };
    }

    try {
      if (IS_LOCAL) {
        console.log('🔧 LOCAL MODE: добавляем в waiting list в mock API');
        const token = localStorage.getItem('token');
        if (!token || !token.startsWith('mock_token_')) {
          return { success: false, message: 'Недействительный токен' };
        }
        const userId = parseInt(token.replace('mock_token_', ''), 10);
        const mockUsers = getMockUsers();
        const userIndex = mockUsers.findIndex((m) => m.id === userId);
        if (userIndex === -1) {
          return { success: false, message: 'Пользователь не найден' };
        }
        const waitingList = mockUsers[userIndex].waitingList || [];
        if (!waitingList.includes(slug)) {
          mockUsers[userIndex].waitingList = [...waitingList, slug];
          saveMockUsers(mockUsers);
        }
        const updatedUser = ensureWaitingList(mockUsers[userIndex]);
        setUser(updatedUser);
        return { success: true, waitingList: updatedUser.waitingList };
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Токен не найден' };
      }

      const response = await fetch('/api/users/waiting-list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ slug }),
      });

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};

      if (!response.ok) {
        console.error('❌ Ошибка добавления в waiting list:', data);
        return {
          success: false,
          message: data.message || 'Не удалось добавить коллекцию в waiting list',
        };
      }

      const updatedWaitingList = Array.isArray(data.waitingList) ? data.waitingList : [];
      const updatedUser = ensureWaitingList({ ...user, waitingList: updatedWaitingList });
      setUser(updatedUser);

      return { success: true, waitingList: updatedWaitingList, collections: data.collections || [] };
    } catch (error) {
      console.error('❌ addToWaitingList error:', error);
      return {
        success: false,
        message: error.message || 'Ошибка при добавлении в waiting list',
      };
    }
  };

  const removeFromWaitingList = async (slug) => {
    console.log('🗑 removeFromWaitingList вызван для slug:', slug);
    if (!slug) {
      return { success: false, message: 'Slug обязателен' };
    }

    if (!user) {
      return { success: false, message: 'Необходимо войти в аккаунт', requireAuth: true };
    }

    try {
      if (IS_LOCAL) {
        const token = localStorage.getItem('token');
        if (!token || !token.startsWith('mock_token_')) {
          return { success: false, message: 'Недействительный токен' };
        }
        const userId = parseInt(token.replace('mock_token_', ''), 10);
        const mockUsers = getMockUsers();
        const userIndex = mockUsers.findIndex((m) => m.id === userId);
        if (userIndex === -1) {
          return { success: false, message: 'Пользователь не найден' };
        }
        const waitingList = mockUsers[userIndex].waitingList || [];
        mockUsers[userIndex].waitingList = waitingList.filter((item) => item !== slug);
        saveMockUsers(mockUsers);
        const updatedUser = ensureWaitingList(mockUsers[userIndex]);
        setUser(updatedUser);
        return { success: true, waitingList: updatedUser.waitingList };
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Токен не найден' };
      }

      const response = await fetch('/api/users/waiting-list', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ slug }),
      });

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};

      if (!response.ok) {
        console.error('❌ Ошибка удаления из waiting list:', data);
        return {
          success: false,
          message: data.message || 'Не удалось удалить коллекцию из waiting list',
        };
      }

      const updatedWaitingList = Array.isArray(data.waitingList) ? data.waitingList : [];
      const updatedUser = ensureWaitingList({ ...user, waitingList: updatedWaitingList });
      setUser(updatedUser);

      return { success: true, waitingList: updatedWaitingList, collections: data.collections || [] };
    } catch (error) {
      console.error('❌ removeFromWaitingList error:', error);
      return {
        success: false,
        message: error.message || 'Ошибка при удалении из waiting list',
      };
    }
  };

  const fetchWaitingListCollections = async () => {
    console.log('📦 fetchWaitingListCollections вызван');
    if (!user) {
      return { success: false, message: 'Необходимо войти в аккаунт', requireAuth: true };
    }

    try {
      if (IS_LOCAL) {
        const waitingList = ensureWaitingList(user)?.waitingList || [];
        const collections = waitingList.map((slug) => ({
          slug,
          title: slug,
          id: slug,
        }));

        return { success: true, waitingList, collections };
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Токен не найден' };
      }

      const response = await fetch('/api/users/waiting-list', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};

      if (!response.ok) {
        console.error('❌ Ошибка получения waiting list:', data);
        return {
          success: false,
          message: data.message || 'Не удалось получить waiting list',
        };
      }

      const waitingList = Array.isArray(data.waitingList) ? data.waitingList : [];
      const updatedUser = ensureWaitingList({ ...user, waitingList });
      setUser(updatedUser);

      return { success: true, waitingList, collections: data.collections || [] };
    } catch (error) {
      console.error('❌ fetchWaitingListCollections error:', error);
      return {
        success: false,
        message: error.message || 'Ошибка при получении waiting list',
      };
    }
  };

  const addToFavorites = async (slug, nftIndex) => {
    console.log('❤️ addToFavorites вызван для:', slug, nftIndex);
    if (!slug) {
      return { success: false, message: 'Slug обязателен' };
    }

    if (!user) {
      console.warn('⚠️ Пользователь не авторизован — favorites недоступен');
      return { success: false, message: 'Необходимо войти в аккаунт', requireAuth: true };
    }

    try {
      if (IS_LOCAL) {
        console.log('🔧 LOCAL MODE: добавляем в favorites в mock API');
        const token = localStorage.getItem('token');
        if (!token || !token.startsWith('mock_token_')) {
          return { success: false, message: 'Недействительный токен' };
        }
        const userId = parseInt(token.replace('mock_token_', ''), 10);
        const mockUsers = getMockUsers();
        const userIndex = mockUsers.findIndex((m) => m.id === userId);
        if (userIndex === -1) {
          return { success: false, message: 'Пользователь не найден' };
        }
        const favorites = Array.isArray(mockUsers[userIndex].favorites) ? mockUsers[userIndex].favorites : [];
        const exists = favorites.some(
          (fav) => fav.slug === slug && typeof fav.nftIndex === 'number' && fav.nftIndex === nftIndex
        );
        if (!exists) {
          mockUsers[userIndex].favorites = [
            ...favorites,
            { slug, nftIndex },
          ];
          saveMockUsers(mockUsers);
        }
        const updatedUser = ensureWaitingList(mockUsers[userIndex]);
        setUser(updatedUser);
        return { success: true, favorites: updatedUser.favorites };
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Токен не найден' };
      }

      const response = await fetch('/api/users/favorites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ slug, nftIndex }),
      });

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};

      if (!response.ok) {
        console.error('❌ Ошибка добавления в favorites:', data);
        return {
          success: false,
          message: data.message || 'Не удалось добавить NFT в favorites',
        };
      }

      const favorites = Array.isArray(data.favorites) ? data.favorites : [];
      const updatedUser = ensureWaitingList({ ...user, favorites });
      setUser(updatedUser);

      return { success: true, favorites, items: data.items || [] };
    } catch (error) {
      console.error('❌ addToFavorites error:', error);
      return {
        success: false,
        message: error.message || 'Ошибка при добавлении в favorites',
      };
    }
  };

  const removeFromFavorites = async (slug, nftIndex) => {
    console.log('💔 removeFromFavorites вызван для:', slug, nftIndex);
    if (!slug) {
      return { success: false, message: 'Slug обязателен' };
    }

    if (!user) {
      return { success: false, message: 'Необходимо войти в аккаунт', requireAuth: true };
    }

    try {
      if (IS_LOCAL) {
        const token = localStorage.getItem('token');
        if (!token || !token.startsWith('mock_token_')) {
          return { success: false, message: 'Недействительный токен' };
        }
        const userId = parseInt(token.replace('mock_token_', ''), 10);
        const mockUsers = getMockUsers();
        const userIndex = mockUsers.findIndex((m) => m.id === userId);
        if (userIndex === -1) {
          return { success: false, message: 'Пользователь не найден' };
        }
        const favorites = Array.isArray(mockUsers[userIndex].favorites) ? mockUsers[userIndex].favorites : [];
        mockUsers[userIndex].favorites = favorites.filter(
          (fav) => !(fav.slug === slug && typeof fav.nftIndex === 'number' && fav.nftIndex === nftIndex)
        );
        saveMockUsers(mockUsers);
        const updatedUser = ensureWaitingList(mockUsers[userIndex]);
        setUser(updatedUser);
        return { success: true, favorites: updatedUser.favorites };
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Токен не найден' };
      }

      const response = await fetch('/api/users/favorites', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ slug, nftIndex }),
      });

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};

      if (!response.ok) {
        console.error('❌ Ошибка удаления из favorites:', data);
        return {
          success: false,
          message: data.message || 'Не удалось удалить NFT из favorites',
        };
      }

      const favorites = Array.isArray(data.favorites) ? data.favorites : [];
      const updatedUser = ensureWaitingList({ ...user, favorites });
      setUser(updatedUser);

      return { success: true, favorites, items: data.items || [] };
    } catch (error) {
      console.error('❌ removeFromFavorites error:', error);
      return {
        success: false,
        message: error.message || 'Ошибка при удалении из favorites',
      };
    }
  };

  const fetchFavorites = async () => {
    console.log('📦 fetchFavorites вызван');
    if (!user) {
      return { success: false, message: 'Необходимо войти в аккаунт', requireAuth: true };
    }

    try {
      if (IS_LOCAL) {
        const favorites = ensureWaitingList(user)?.favorites || [];
        const items = favorites.map((fav) => ({
          slug: fav.slug,
          nftIndex: fav.nftIndex ?? null,
          collectionTitle: fav.slug,
          nft: null,
        }));

        return { success: true, favorites, items };
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Токен не найден' };
      }

      const response = await fetch('/api/users/favorites', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('📡 fetchFavorites response status:', response.status);
      const text = await response.text();
      console.log('📡 fetchFavorites response text:', text);
      const data = text ? JSON.parse(text) : {};
      console.log('📡 fetchFavorites parsed data:', data);

      if (!response.ok) {
        console.error('❌ Ошибка получения favorites:', data);
        return {
          success: false,
          message: data.message || 'Не удалось получить favorites',
        };
      }

      const favorites = Array.isArray(data.favorites) ? data.favorites : [];
      const items = Array.isArray(data.items) ? data.items : [];
      console.log('✅ fetchFavorites success, favorites:', favorites.length, 'items:', items.length);
      
      const updatedUser = ensureWaitingList({ ...user, favorites });
      setUser(updatedUser);

      return { success: true, favorites, items };
    } catch (error) {
      console.error('❌ fetchFavorites error:', error);
      return {
        success: false,
        message: error.message || 'Ошибка при получении favorites',
      };
    }
  };

  const addToCollections = async (slug) => {
    console.log('❤️ addToCollections вызван для:', slug);
    if (!slug) {
      return { success: false, message: 'Slug обязателен' };
    }

    if (!user) {
      console.warn('⚠️ Пользователь не авторизован — collections недоступен');
      return { success: false, message: 'Необходимо войти в аккаунт', requireAuth: true };
    }

    try {
      if (IS_LOCAL) {
        console.log('🔧 LOCAL MODE: добавляем в collections в mock API');
        const token = localStorage.getItem('token');
        if (!token || !token.startsWith('mock_token_')) {
          return { success: false, message: 'Недействительный токен' };
        }
        const userId = parseInt(token.replace('mock_token_', ''), 10);
        const mockUsers = getMockUsers();
        const userIndex = mockUsers.findIndex((m) => m.id === userId);
        if (userIndex === -1) {
          return { success: false, message: 'Пользователь не найден' };
        }
        const collections = Array.isArray(mockUsers[userIndex].collections) ? mockUsers[userIndex].collections : [];
        const exists = collections.some((col) => col.slug === slug);
        if (!exists) {
          mockUsers[userIndex].collections = [
            ...collections,
            { slug },
          ];
          saveMockUsers(mockUsers);
        }
        const updatedUser = ensureWaitingList(mockUsers[userIndex]);
        setUser(updatedUser);
        return { success: true, collections: updatedUser.collections || [] };
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Токен не найден' };
      }

      const response = await fetch('/api/users/collections', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ slug }),
      });

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};

      if (!response.ok) {
        console.error('❌ Ошибка добавления в collections:', data);
        return {
          success: false,
          message: data.message || 'Не удалось добавить коллекцию в избранное',
        };
      }

      const collections = Array.isArray(data.collections) ? data.collections : [];
      const updatedUser = ensureWaitingList({ ...user, collections });
      setUser(updatedUser);

      return { success: true, collections, items: data.items || [] };
    } catch (error) {
      console.error('❌ addToCollections error:', error);
      return {
        success: false,
        message: error.message || 'Ошибка при добавлении в collections',
      };
    }
  };

  const removeFromCollections = async (slug) => {
    console.log('💔 removeFromCollections вызван для:', slug);
    if (!slug) {
      return { success: false, message: 'Slug обязателен' };
    }

    if (!user) {
      return { success: false, message: 'Необходимо войти в аккаунт', requireAuth: true };
    }

    try {
      if (IS_LOCAL) {
        const token = localStorage.getItem('token');
        if (!token || !token.startsWith('mock_token_')) {
          return { success: false, message: 'Недействительный токен' };
        }
        const userId = parseInt(token.replace('mock_token_', ''), 10);
        const mockUsers = getMockUsers();
        const userIndex = mockUsers.findIndex((m) => m.id === userId);
        if (userIndex === -1) {
          return { success: false, message: 'Пользователь не найден' };
        }
        const collections = Array.isArray(mockUsers[userIndex].collections) ? mockUsers[userIndex].collections : [];
        mockUsers[userIndex].collections = collections.filter((col) => col.slug !== slug);
        saveMockUsers(mockUsers);
        const updatedUser = ensureWaitingList(mockUsers[userIndex]);
        setUser(updatedUser);
        return { success: true, collections: updatedUser.collections || [] };
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Токен не найден' };
      }

      const response = await fetch('/api/users/collections', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ slug }),
      });

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};

      if (!response.ok) {
        console.error('❌ Ошибка удаления из collections:', data);
        return {
          success: false,
          message: data.message || 'Не удалось удалить коллекцию из избранного',
        };
      }

      const collections = Array.isArray(data.collections) ? data.collections : [];
      const updatedUser = ensureWaitingList({ ...user, collections });
      setUser(updatedUser);

      return { success: true, collections, items: data.items || [] };
    } catch (error) {
      console.error('❌ removeFromCollections error:', error);
      return {
        success: false,
        message: error.message || 'Ошибка при удалении из collections',
      };
    }
  };

  const fetchCollections = async () => {
    console.log('📦 fetchCollections вызван');
    if (!user) {
      return { success: false, message: 'Необходимо войти в аккаунт', requireAuth: true };
    }

    try {
      if (IS_LOCAL) {
        const collections = ensureWaitingList(user)?.collections || [];
        const items = collections.map((col) => ({
          slug: col.slug,
          title: col.slug,
        }));

        return { success: true, collections, items };
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, message: 'Токен не найден' };
      }

      const response = await fetch('/api/users/collections', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('📡 fetchCollections response status:', response.status);
      const text = await response.text();
      console.log('📡 fetchCollections response text:', text);
      const data = text ? JSON.parse(text) : {};
      console.log('📡 fetchCollections parsed data:', data);

      if (!response.ok) {
        console.error('❌ Ошибка получения collections:', data);
        return {
          success: false,
          message: data.message || 'Не удалось получить collections',
        };
      }

      const collections = Array.isArray(data.collections) ? data.collections : [];
      const items = Array.isArray(data.items) ? data.items : [];
      console.log('✅ fetchCollections success, collections:', collections.length, 'items:', items.length);
      
      const updatedUser = ensureWaitingList({ ...user, collections });
      setUser(updatedUser);

      return { success: true, collections, items };
    } catch (error) {
      console.error('❌ fetchCollections error:', error);
      return {
        success: false,
        message: error.message || 'Ошибка при получении collections',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    console.log('🚪 Выход выполнен');
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    addToWaitingList,
    removeFromWaitingList,
    fetchWaitingListCollections,
    addToFavorites,
    removeFromFavorites,
    fetchFavorites,
    addToCollections,
    removeFromCollections,
    fetchCollections,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};